// Pre-filled scenario presets for common Ontario CCPC business types.
// Numbers are illustrative averages — advisors will tune for each real client.
// `showcase` figures are pre-computed from the calc engine (see backend/generate_presets.py).

export const PRESETS = [
  {
    id: "homeowner",
    label: "Average Homeowner",
    blurb: "Personal finances \u2014 household income, modest savings, family vehicles.",
    icon: "House",
    note: "Personal-tax bracket, not CCPC. IBC works personally too.",
    showcase: { leak: 2702, gain5: 21135, gain10: 66952 },
    inputs: {
      passive_return_rate: 0.04,
      passive_tax_rate: 0.3,
      avg_loan_interest_rate: 0.069,
      avg_loan_term_years: 5,
      retention_probability_boost: 0,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 18000,
      num_vehicles: 2,
      avg_monthly_payment: 550,
      num_key_employees: 0,
      annual_retention_bonus: 0,
      turnover_cost_per_employee: 0,
    },
  },
  {
    id: "restaurant",
    label: "Restaurant Owner",
    blurb: "Independent restaurant \u2014 delivery vehicle, GM, head chef, sous chef.",
    icon: "ForkKnife",
    showcase: { leak: 4851, gain5: 224221, gain10: 536693 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 60000,
      num_vehicles: 2,
      avg_monthly_payment: 600,
      num_key_employees: 3,
      annual_retention_bonus: 8000,
      turnover_cost_per_employee: 35000,
    },
  },
  {
    id: "landscaper",
    label: "Landscaping Co.",
    blurb: "Seasonal crew, fleet of trucks + trailers, equipment loans.",
    icon: "Tree",
    showcase: { leak: 18999, gain5: 314927, gain10: 806359 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 120000,
      num_vehicles: 8,
      avg_monthly_payment: 850,
      num_key_employees: 2,
      annual_retention_bonus: 12000,
      turnover_cost_per_employee: 45000,
    },
  },
  {
    id: "contractor",
    label: "Contractor / Roofer",
    blurb: "Like Bay Roofing \u2014 15 trucks, 4 foremen, $200K/yr retained.",
    icon: "Hammer",
    showcase: { leak: 37827, gain5: 700693, gain10: 1695560 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 200000,
      num_vehicles: 15,
      avg_monthly_payment: 950,
      num_key_employees: 4,
      annual_retention_bonus: 15000,
      turnover_cost_per_employee: 60000,
    },
  },
  {
    id: "plumber",
    label: "Plumbing & HVAC",
    blurb: "Service trucks, lead techs, on-call premium pay.",
    icon: "Wrench",
    showcase: { leak: 14387, gain5: 414790, gain10: 991376 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 110000,
      num_vehicles: 6,
      avg_monthly_payment: 800,
      num_key_employees: 3,
      annual_retention_bonus: 14000,
      turnover_cost_per_employee: 55000,
    },
  },
  {
    id: "electrician",
    label: "Electrical Contractor",
    blurb: "Service vans, licensed electricians on payroll, tool + van financing.",
    icon: "Lightning",
    showcase: { leak: 12099, gain5: 376274, gain10: 899636 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 100000,
      num_vehicles: 5,
      avg_monthly_payment: 780,
      num_key_employees: 3,
      annual_retention_bonus: 13000,
      turnover_cost_per_employee: 50000,
    },
  },
  {
    id: "mechanic",
    label: "Auto Repair Shop",
    blurb: "2 service trucks, 4 mechanics, lift + diagnostic equipment financed.",
    icon: "Car",
    showcase: { leak: 6642, gain5: 354411, gain10: 841201 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 90000,
      num_vehicles: 2,
      avg_monthly_payment: 750,
      num_key_employees: 4,
      annual_retention_bonus: 10000,
      turnover_cost_per_employee: 40000,
    },
  },
  {
    id: "trucking",
    label: "Trucking & Logistics",
    blurb: "Financed tractor units and trailers, long-haul drivers you can't lose.",
    icon: "Truck",
    showcase: { leak: 125160, gain5: 1140248, gain10: 2648214 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.085,
      avg_loan_term_years: 6,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 250000,
      num_vehicles: 20,
      avg_monthly_payment: 2200,
      num_key_employees: 5,
      annual_retention_bonus: 12000,
      turnover_cost_per_employee: 40000,
    },
  },
  {
    id: "construction",
    label: "Construction & Excavation",
    blurb: "Heavy equipment loans \u2014 excavators, loaders, dump trucks \u2014 plus site supers.",
    icon: "HardHat",
    showcase: { leak: 66590, gain5: 1095287, gain10: 2631836 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.082,
      avg_loan_term_years: 6,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 300000,
      num_vehicles: 12,
      avg_monthly_payment: 1800,
      num_key_employees: 5,
      annual_retention_bonus: 18000,
      turnover_cost_per_employee: 70000,
    },
  },
  {
    id: "dental",
    label: "Dental / Medical Practice",
    blurb: "Financed clinical equipment, associates and hygienists worth retaining.",
    icon: "Tooth",
    showcase: { leak: 8633, gain5: 545568, gain10: 1355892 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.069,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 180000,
      num_vehicles: 1,
      avg_monthly_payment: 900,
      num_key_employees: 4,
      annual_retention_bonus: 15000,
      turnover_cost_per_employee: 60000,
    },
  },
  {
    id: "farm",
    label: "Farm & Agriculture",
    blurb: "Tractors, combines and grain trucks on dealer financing; seasonal cash cycles.",
    icon: "Tractor",
    showcase: { leak: 41666, gain5: 407420, gain10: 1020762 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.075,
      avg_loan_term_years: 7,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 140000,
      num_vehicles: 9,
      avg_monthly_payment: 1500,
      num_key_employees: 2,
      annual_retention_bonus: 10000,
      turnover_cost_per_employee: 35000,
    },
  },
  {
    id: "leasingco",
    label: "Equipment Leasing Co.",
    blurb: "A financed fleet on the books earning spreads \u2014 with carrying costs eating the margin.",
    icon: "HandCoins",
    showcase: { leak: 87460, gain5: 1007522, gain10: 2529850 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 350000,
      num_vehicles: 25,
      avg_monthly_payment: 1400,
      num_key_employees: 4,
      annual_retention_bonus: 15000,
      turnover_cost_per_employee: 55000,
    },
  },
  {
    id: "manufacturing",
    label: "Manufacturing Facility",
    blurb: "Production lines and forklifts financed, skilled operators and plant managers.",
    icon: "Factory",
    showcase: { leak: 74448, gain5: 1415356, gain10: 3419062 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.075,
      avg_loan_term_years: 6,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 400000,
      num_vehicles: 10,
      avg_monthly_payment: 2500,
      num_key_employees: 8,
      annual_retention_bonus: 15000,
      turnover_cost_per_employee: 65000,
    },
  },
  {
    id: "corporate",
    label: "Multi-Location Corporation",
    blurb: "Multiple locations, 30+ financed vehicles, a deep bench of key managers.",
    icon: "Buildings",
    showcase: { leak: 101621, gain5: 2531441, gain10: 6166037 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.075,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 750000,
      num_vehicles: 30,
      avg_monthly_payment: 1200,
      num_key_employees: 12,
      annual_retention_bonus: 20000,
      turnover_cost_per_employee: 80000,
    },
  },
  {
    id: "smallbiz",
    label: "Small Business Owner",
    blurb: "Generic CCPC \u2014 4 company vehicles, 2 key employees.",
    icon: "Briefcase",
    showcase: { leak: 9003, gain5: 251639, gain10: 620947 },
    inputs: {
      passive_return_rate: 0.05,
      passive_tax_rate: 0.5017,
      avg_loan_interest_rate: 0.079,
      avg_loan_term_years: 5,
      retention_probability_boost: 0.5,
      dividend_rate: 0.0635,
      horizon_years: 10,
      annual_contribution: 80000,
      num_vehicles: 4,
      avg_monthly_payment: 700,
      num_key_employees: 2,
      annual_retention_bonus: 12000,
      turnover_cost_per_employee: 50000,
    },
  },
];

export const PRESET_BY_ID = Object.fromEntries(PRESETS.map((p) => [p.id, p]));
